import React, { useState, useEffect } from 'react';
import { Accordion, AccordionSummary, AccordionDetails, Typography, Container, Box, CircularProgress, IconButton } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import axios from 'axios';
import 'react-image-lightbox/style.css';
import Lightbox from 'react-image-lightbox';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

const albums = [
  {
    id: '72177720315281554',
    title: '06/03/2024 - Mobilização Municipalista',
  },
  {
    id: '72177720311713494',
    title: '04/10/2023 - Mobilização Municipalista',
  },
  {
    id: '72177720309545258',
    title: '04/07/2023 - Encontro Municipalista',
  },
];

const apiKey = 'd47b155c521b959e206f89235f04e2c7';
const userId = '89727608@N03';

const FlickrAccordion = () => {
  const [albumImages, setAlbumImages] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(albums[0].id);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxImage, setLightboxImage] = useState(null);
  const [highResImage, setHighResImage] = useState(null);
  const [imageLoading, setImageLoading] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  useEffect(() => {
    const fetchImages = async () => {
      try {
        const promises = albums.map(async (album) => {
          const response = await axios.get(
            `https://api.flickr.com/services/rest/?method=flickr.photosets.getPhotos&api_key=${apiKey}&photoset_id=${album.id}&user_id=${userId}&format=json&nojsoncallback=1`
          );

          if (response.data && response.data.photoset && response.data.photoset.photo) {
            return {
              albumId: album.id,
              photos: response.data.photoset.photo.map(photo => ({
                ...photo,
                thumbnailUrl: `https://live.staticflickr.com/${photo.server}/${photo.id}_${photo.secret}_q.jpg`,
                highResUrl: `https://live.staticflickr.com/${photo.server}/${photo.id}_${photo.secret}_b.jpg`,
              })),
            };
          } else {
            console.error(`No photos found for album ID ${album.id}`);
            return {
              albumId: album.id,
              photos: [],
            };
          }
        });

        const results = await Promise.all(promises);
        const images = results.reduce((acc, result) => {
          acc[result.albumId] = result.photos;
          return acc;
        }, {});
        setAlbumImages(images);
      } catch (err) {
        console.error('Error fetching images:', err);
        setError('Error fetching images. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchImages();
  }, []);

  const handleChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  const openLightbox = (imageSrc, highResSrc, index) => {
    setLightboxImage(imageSrc);
    setHighResImage(highResSrc);
    setCurrentImageIndex(index);
    setLightboxOpen(true);
    preloadImage(highResSrc);
  };

  const preloadImage = (src) => {
    setImageLoading(true);
    const img = new Image();
    img.src = src;
    img.onload = () => {
      setHighResImage(src);
      setImageLoading(false);
    };
  };

  const navigateToPreviousImage = () => {
    const newIndex = (currentImageIndex - 1 + albumImages[expanded].length) % albumImages[expanded].length;
    setCurrentImageIndex(newIndex);
    setLightboxImage(albumImages[expanded][newIndex].thumbnailUrl);
    setHighResImage(albumImages[expanded][newIndex].highResUrl);
    preloadImage(albumImages[expanded][newIndex].highResUrl);
  };

  const navigateToNextImage = () => {
    const newIndex = (currentImageIndex + 1) % albumImages[expanded].length;
    setCurrentImageIndex(newIndex);
    setLightboxImage(albumImages[expanded][newIndex].thumbnailUrl);
    setHighResImage(albumImages[expanded][newIndex].highResUrl);
    preloadImage(albumImages[expanded][newIndex].highResUrl);
  };

  if (loading) {
    return <Typography style={{ minHeight: '63vh', display: 'flex', justifyContent: 'center', alignItems: 'center', }}><CircularProgress /></Typography>;
  }

  if (error) {
    return <Typography>{error}</Typography>;
  }

  return (
    <Container maxWidth="xl" style={{ marginTop: '2rem', minHeight: '60vh' }}>
      <h1 style={{ textAlign: 'center', fontWeight: '700', fontSize: '28px', margin: '2em' }}>Galeria</h1>
      {albums.map((album) => (
        <Accordion
          key={album.id}
          expanded={expanded === album.id}
          onChange={handleChange(album.id)}
          style={{ alignItems: 'center', alignContent: 'center' }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography>{album.title}</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '1rem', justifyContent: 'center' }}>
              {albumImages[album.id] && albumImages[album.id].length > 0 ? (
                albumImages[album.id].map((photo, index) => (
                  <Box
                    key={photo.id}
                    sx={{ width: '150px', height: '150px', overflow: 'hidden', cursor: 'pointer' }}
                    onClick={() => openLightbox(photo.thumbnailUrl, photo.highResUrl, index)}
                  >
                    <img
                      src={photo.thumbnailUrl}
                      alt={photo.title}
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  </Box>
                ))
              ) : (
                <Typography>No images found for this album.</Typography>
              )}
            </Box>
          </AccordionDetails>
        </Accordion>
      ))}
      {lightboxOpen && (
        <Lightbox
          mainSrc={highResImage}
          nextSrc={albumImages[expanded][(currentImageIndex + 1) % albumImages[expanded].length].highResUrl}
          prevSrc={albumImages[expanded][(currentImageIndex + albumImages[expanded].length - 1) % albumImages[expanded].length].highResUrl}
          onCloseRequest={() => setLightboxOpen(false)}
          onMovePrevRequest={navigateToPreviousImage}
          onMoveNextRequest={navigateToNextImage}
          imageTitle={`Image ${currentImageIndex + 1} of ${albumImages[expanded].length}`}
          imageCaption={albumImages[expanded][currentImageIndex].title}
          loader={<CircularProgress />}
          toolbarButtons={[
            <IconButton onClick={navigateToPreviousImage}><ArrowBackIcon /></IconButton>,
            <IconButton onClick={navigateToNextImage}><ArrowForwardIcon /></IconButton>
          ]}
        />
      )}
    </Container>
  );
};

export default FlickrAccordion;
